import { computed, ComputedRef, onActivated, onDeactivated, onUnmounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useRolePermission } from '@/common/permission/permission.utils';
import {
  CurrentDashboardDetailPermission,
  DetailTypeToPermissionKey,
  type SlideTabPanelInfo,
  TOTAL_DETAIL,
  useSlideDetailStore,
  useSplitSlideDetailStore,
} from '@/common/stores/slide-detail';
import { storeToRefs } from 'pinia';
import { useInternational } from '@/common/locale';
import { getLocaleKey } from '@/common/utils/commonUtils';
import type { RolePermissionKeyType } from '@/config/views/permission/rolePermission/rolePermission.type';
import { TXN_TYPES } from '@/application/utils/define';
import { openBrowserPopup } from '@/common/utils/browserPopupUtils';
import { webStorageController } from '@/common/utils/browser';
import { useStandardVariablesStore } from '@/dashboard/stores/global-variables/standard-variables';
import { useFilterActiveSessionStore } from '@/common/stores/filter-active-session';

export interface Props {
  isVisible: boolean;
  isLoading?: boolean;
  hasExpand?: boolean;
}

export interface Emit {
  (e: 'update:is-visible', value: boolean): void;
}

export const setup = (props: Props, emit: Emit) => {
  const route = useRoute();
  const { t } = useInternational();
  const slideDetailStore = useSlideDetailStore();
  const filterActiveSessionStore = useFilterActiveSessionStore();
  const {
    initTabPanel,
    setSelectedSlideTabPanel,
    addSlideDetailHistory,
    deleteLastSlideDetailHistory,
    setOpened,
    deleteSlideTabPanel,
    onClickExcess,
  } = slideDetailStore;
  const {
    isDetailLoading,
    detailComp,
    selectedSlideTabPanel,
    slideTabPanel,
    slideDetailHistory,
    isOpened,
    isExcess,
  } = storeToRefs(slideDetailStore);

  const splitSlideDetailStore = useSplitSlideDetailStore();
  const { initSplitTabPanel } = splitSlideDetailStore;
  const {
    isOpened: isSplitOpened,
    detailComp: splitDetailComp,
    isDetailLoading: isSplitLoading,
    selectedSlideTabPanel: splitTabPanel,
  } = storeToRefs(splitSlideDetailStore);

  const { isPermissionDenied } = useRolePermission();
  const isDetailSlidePermissionDenied = (rolePermissionKey: RolePermissionKeyType) => {
    return isPermissionDenied({
      type: 'detailSlide',
      showError: false,
      rolePermissionKey,
    });
  };

  // SlideDetail은 뷰마다 개별 마운트되면서 전역 스토어(isOpened/detailComp)를 공유한다.
  // keep-alive 뷰(PA 계열)는 라우트 이탈 시 unmount가 아니라 deactivate만 되어 인스턴스가
  // detached 트리에 살아남는다. 이때 여러 뷰가 is-visible을 동일한 전역 스토어(isOpened 미러,
  // 예: usePaSlideDetailStore.isSlideDetailOpened)에 바인딩하므로, 다른 뷰에서 디테일을 열면
  // 비활성 인스턴스의 props.isVisible도 함께 true가 되어 detailComp가 중복 마운트된다
  // (폴링·API 2배). isActive로 렌더를 게이트해 비활성 인스턴스는 detailComp를 마운트하지 않는다.
  const isActive = ref(true);

  const isShow = computed({
    get: () => props.isVisible && isActive.value,
    set: (value) => {
      emit('update:is-visible', value);
    },
  });

  const isOpenNav = ref(false);

  const isShowSplit = computed(() => {
    return isSplitOpened.value && !!splitDetailComp.value;
  });

  const closeSplit = () => {
    initSplitTabPanel();
  };

  const onSlideTabChange = (item: SlideTabPanelInfo, isHistoryBack = false) => {
    if (item.value === selectedSlideTabPanel.value?.value) return;

    setSelectedSlideTabPanel(item);

    if (!isHistoryBack) {
      addSlideDetailHistory(item);
    }
  };

  const onNavToggle = () => {
    isOpenNav.value = !isOpenNav.value;
  };

  const onHistoryBack = () => {
    if (slideDetailHistory.value.length === 1) return;

    const popped = deleteLastSlideDetailHistory();
    if (popped?.value != null) {
      filterActiveSessionStore.clearBySlideValue(popped.value);
    }
    const lastHistory = slideDetailHistory.value[slideDetailHistory.value.length - 1];
    onSlideTabChange(lastHistory, true);
  };

  const getLocalizedTitle = (panelInfo) => {
    const { type, name } = panelInfo || {};
    const exactType = (type || '').replace(' List', '');
    let localizedType = exactType ? t(`WORD.${getLocaleKey(exactType)}`, exactType) : exactType;
    const detailItemName = name ? `: ${name}` : '';
    if (localizedType === TOTAL_DETAIL.BROWSER_ERROR_TRACKING) {
      return t('WORD.BROWSER_ERROR_ANALYSIS');
    }
    if (localizedType === TOTAL_DETAIL.WEB_APPLICATION) {
      localizedType = t('WORD.APPLICATION');
    }
    if (localizedType === TOTAL_DETAIL.USER_MONITORING_SESSION_ANALYSIS) {
      return t('WORD.SESSION_ANALYSIS');
    }
    if (localizedType === TOTAL_DETAIL.USER_MONITORING_SESSION_REPLAY) {
      return t('WORD.SESSION_REPLAY');
    }
    if (localizedType === TOTAL_DETAIL.USER_MONITORING_USER_EVENT) {
      return name ?? t('WORD.USER_EVENT');
    }
    if (localizedType === TOTAL_DETAIL.SQL) {
      return name?.startsWith('Query ID') || name?.startsWith('SQL ID')
        ? (name ?? '')
        : `${localizedType}${detailItemName}`;
    }
    if (localizedType === TOTAL_DETAIL.SLOW_QUERY) {
      return `Slow Query ${t('WORD.DETAIL')}`;
    }
    if (localizedType === TOTAL_DETAIL.ACTIVE_QUERY) {
      return `Activity Name${detailItemName}`;
    }
    if (localizedType === TOTAL_DETAIL.SHARDED_CLUSTER) {
      localizedType = `${t('WORD.SHARDED')} ${t('WORD.CLUSTER')}`;
    }
    if (type === TOTAL_DETAIL.ALERT_HISTORY) {
      return `${t('WORD.HOME.TOTAL_ALERT')}`;
    }
    if (type === TOTAL_DETAIL.TRANSACTION_INFO) {
      return t('WORD.TRANSACTION');
    }
    if (type === TOTAL_DETAIL.TRANSACTION) {
      const title =
        panelInfo?.transaction?.txnType === TXN_TYPES.WSM
          ? t('WORD.WEB_TRANSACTIONS')
          : t('WORD.TRANSACTION');
      return `${title}${detailItemName}`;
    }
    if (type === TOTAL_DETAIL.KEYWORD_LOG) {
      return `${t('WORD.LOG')}${detailItemName}`;
    }
    if (type === TOTAL_DETAIL.EVENT_ALERT) {
      return `${t('WORD.ALERT')}: ${name}`;
    }
    if (type === TOTAL_DETAIL.CONTAINER_HOST || type === TOTAL_DETAIL.K8S_CONTAINER) {
      return `${t('WORD.CONTAINER')}${detailItemName}`;
    }
    if (type === TOTAL_DETAIL.ACTIVE_TRANSACTION) {
      return t('WORD.ACTIVE_TRANSACTION');
    }
    return `${localizedType}${detailItemName}`;
  };

  const localizedTitle: ComputedRef<string> = computed(
    () => selectedSlideTabPanel.value?.title || getLocalizedTitle(selectedSlideTabPanel.value),
  );

  const getNavTitle = ({
    type,
    name,
    title,
  }: { type?: string; name?: string; title?: string } = {}): string =>
    title || getLocalizedTitle({ type, name });

  const closeSlide = () => {
    initTabPanel();
    filterActiveSessionStore.clearAll();
    isOpenNav.value = false;
    isShow.value = false;
  };

  const SLIDE_DETAIL_STORAGE_KEY = 'slideDetailPanelInfo';
  const SLIDE_DETAIL_STD_VARS_KEY = 'slideDetailStandardVariables';
  const SLIDE_DETAIL_POPUP_SIZE = { width: 1200, height: 800 };

  const onClickOpenPopup = () => {
    if (!selectedSlideTabPanel.value) {
      return;
    }

    const { splitInfo: _splitInfo, ...panelData } = selectedSlideTabPanel.value;
    webStorageController.setItem({
      type: 'session',
      key: SLIDE_DETAIL_STORAGE_KEY,
      value: JSON.stringify(panelData),
    });

    const standardVarsStore = useStandardVariablesStore();
    if (standardVarsStore.selectedStandardVariables.length > 0) {
      webStorageController.setItem({
        type: 'session',
        key: SLIDE_DETAIL_STD_VARS_KEY,
        value: JSON.stringify(standardVarsStore.selectedStandardVariables),
      });
    }

    openBrowserPopup({
      query: {
        viewName: 'slideDetail',
        title: localizedTitle.value,
      },
      popupStyle: SLIDE_DETAIL_POPUP_SIZE,
      isSingle: false,
    });
  };

  watch(
    () => props.isVisible,
    (isVisible) => {
      if (isVisible) {
        const rolePermissionKey = selectedSlideTabPanel.value?.type
          ? DetailTypeToPermissionKey[selectedSlideTabPanel.value.type]
          : undefined;
        if (
          !rolePermissionKey ||
          isDetailSlidePermissionDenied(
            selectedSlideTabPanel.value?.rolePermissionKey || rolePermissionKey,
          ) ||
          (route.query.rootMenu &&
            isDetailSlidePermissionDenied(
              CurrentDashboardDetailPermission[`${route.query.rootMenu}`],
            ))
        ) {
          console.error('Permission Denied.');
          isShow.value = false;
          return;
        }
      }

      setOpened(isVisible);
      if (!isVisible) {
        closeSlide();
      }
    },
  );

  watch(isOpened, (value) => {
    // 비활성(deactivated) 인스턴스는 부모 v-model 동기화(emit)도 차단한다.
    if (value && !isActive.value) return;
    isShow.value = value;
  });

  onUnmounted(() => {
    setOpened(false);
    initSplitTabPanel();
    filterActiveSessionStore.clearAll();
  });

  onActivated(() => {
    isActive.value = true;
  });

  onDeactivated(() => {
    isActive.value = false;
    isShow.value = false;
    // isShow emit → 부모 v-model → props.isVisible watch 체인에 기대지 않고
    // 전역 열림 상태·split 패널·활성 세션 필터를 직접 정리한다 (unmount 시 정리와 동일).
    // split을 남겨두면 다른 뷰에서 메인 디테일을 열 때 stale split 패널이 함께 노출된다.
    setOpened(false);
    initSplitTabPanel();
    filterActiveSessionStore.clearAll();
  });

  return {
    isShow,
    isOpenNav,
    isDetailLoading,
    detailComp,
    selectedSlideTabPanel,
    slideTabPanel,
    slideDetailHistory,
    onSlideTabChange,
    onNavToggle,
    onHistoryBack,
    localizedTitle,
    getNavTitle,
    t,
    closeSlide,
    getLocalizedTitle,
    deleteSlideTabPanel,
    isExcess,
    onClickExcess,

    onClickOpenPopup,

    isShowSplit,
    splitDetailComp,
    isSplitLoading,
    splitTabPanel,
    closeSplit,
  };
};
